import { fetchWithPolicy, readErrorData, SessionEpoch } from "./http-policy.mjs";

export class ApiError extends Error {
  public data: Record<string, unknown> | null;
  public status: number;

  constructor(
    status: number,
    message: string,
    data?: Record<string, unknown> | null,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data ?? null;
  }
}

export const STAFF_SESSION_EXPIRED_EVENT =
  "evolys:staff-session-expired";

const staffSession = new SessionEpoch();

function notifyStaffSessionExpired(status: number): void {
  if (status !== 401 || typeof window === "undefined") return;
  staffSession.invalidate();
  window.dispatchEvent(new Event(STAFF_SESSION_EXPIRED_EVENT));
}

function isAnonymousSecureResultLink(path: string): boolean {
  return path.startsWith("/api/portal/result-links/");
}

function firstValidationError(
  data: Record<string, unknown> | null,
): string | undefined {
  if (!data?.errors || typeof data.errors !== "object") return undefined;

  for (const messages of Object.values(
    data.errors as Record<string, unknown>,
  )) {
    if (Array.isArray(messages)) {
      const message = messages.find(
        (value): value is string =>
          typeof value === "string" && value.trim().length > 0,
      );
      if (message) return message;
    } else if (typeof messages === "string" && messages.trim()) {
      return messages;
    }
  }

  return undefined;
}

function apiErrorMessage(
  status: number,
  data: Record<string, unknown> | null,
): string {
  const candidates = [
    data?.message,
    data?.detail,
    firstValidationError(data),
    data?.error,
    data?.title,
  ];
  const message = candidates.find(
    (value): value is string =>
      typeof value === "string" && value.trim().length > 0,
  );
  return message?.trim() ?? `API Error: ${status}`;
}

export class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  invalidateSession(): void { staffSession.invalidate(); }

  private async response(endpoint: string, options: RequestInit, format: "json" | "blob" = "json"): Promise<unknown> {
    const epoch = staffSession.capture();
    const assertCurrent = () => {
      if (!staffSession.isCurrent(epoch)) throw new ApiError(401, "Session changed. Reload the record.");
    };
    const response = await fetchWithPolicy(this.baseUrl + endpoint, {
      ...options, credentials: "include", cache: "no-store",
    }, { retryGet: true, assertCurrent });
    if (!response.ok) {
      const data = await readErrorData(response);
      assertCurrent();
      if (!isAnonymousSecureResultLink(endpoint)) notifyStaffSessionExpired(response.status);
      throw new ApiError(response.status, apiErrorMessage(response.status, data), data);
    }
    const data = response.status === 204 ? undefined : format === "blob" ? await response.blob() : await response.json();
    assertCurrent();
    return data;
  }

  private request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    return this.response(endpoint, { ...options, headers: { "Content-Type": "application/json", ...options.headers } }) as Promise<T>;
  }

  get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: "GET" });
  }

  post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  put<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  patch<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: "DELETE" });
  }

  sendBeacon(endpoint: string): boolean {
    if (typeof navigator === "undefined" || !navigator.sendBeacon) {
      return false;
    }

    return navigator.sendBeacon(`${this.baseUrl}${endpoint}`);
  }

  getBlob(path: string): Promise<Blob> {
    return this.response(path, { method: "GET" }, "blob") as Promise<Blob>;
  }

  postBlob(path: string, data?: unknown): Promise<Blob> {
    return this.response(path, { method: "POST", headers: { "Content-Type": "application/json" },
      body: data !== undefined ? JSON.stringify(data) : undefined }, "blob") as Promise<Blob>;
  }

  postFormData<T>(endpoint: string, formData: FormData): Promise<T> {
    // The browser supplies the multipart boundary.
    return this.response(endpoint, { method: "POST", body: formData }) as Promise<T>;
  }
}
